import React, { useState, useEffect } from 'react';

const isTouch = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const MessageBubble = ({ message, isStreaming = false }) => {
  const isUser = message.role === 'user';
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // For Eunoia messages: fade in when message is complete (not streaming)
  useEffect(() => {
    if (isUser) {
      setIsVisible(true);
      return;
    }
    if (!isStreaming && message.content) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUser, isStreaming, message.content]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (e) { /* silently fail */ }
  };

  const iconOpacity = isCopied ? 1 : (isTouch ? 0.3 : (isHovered ? 1 : 0));

  const copyBtnBase = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px',
    lineHeight: 1,
    color: isCopied ? '#C48A4A' : 'rgba(245, 230, 211, 0.4)',
    opacity: iconOpacity,
    transition: 'opacity 0.2s ease, color 0.2s ease',
    minWidth: 0,
    minHeight: 0,
    alignSelf: 'flex-start',
    marginTop: '6px',
  };

  // User message - appears instantly, right-aligned
  if (isUser) {
    return (
      <div
        data-testid={`message-${message.role}`}
        className="message-wrapper"
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className="user-message"
          style={{
            display: 'flex',
            flexDirection: 'column',
            background: 'rgba(196, 137, 74, 0.15)',
            border: '0.5px solid rgba(196, 137, 74, 0.3)',
            borderRadius: '18px 18px 4px 18px',
            padding: '10px 14px',
            maxWidth: '75%',
          }}
        >
          <p
            style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: 'clamp(15px, 4vw, 17px)',
              lineHeight: 1.65,
              color: 'rgba(255, 243, 225, 0.9)',
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}
          >
            {message.content}
          </p>
          <button onClick={handleCopy} title="Copy message" style={copyBtnBase}>
            {isCopied ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>
      </div>
    );
  }

  // Don't render Eunoia message while streaming - show breathing indicator instead
  if (isStreaming) {
    return null;
  }

  // Eunoia message - reveal with fade + slide when complete
  return (
    <div
      data-testid={`message-${message.role}`}
      className="eunoia-message-wrapper"
      style={{
        marginBottom: '20px',
        opacity: isVisible ? undefined : 0,
        animation: isVisible ? 'messageReveal 0.4s ease forwards' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="eunoia-message"
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '2px solid rgba(196, 137, 74, 0.4)',
          paddingLeft: '12px',
          maxWidth: '82%',
        }}
      >
        <p
          style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: 'clamp(15px, 4vw, 17px)',
            fontStyle: 'italic',
            fontWeight: 400,
            lineHeight: 1.8,
            color: 'rgba(255, 243, 225, 0.75)',
            margin: 0,
            whiteSpace: 'pre-wrap',
          }}
        >
          {message.content}
        </p>
        <button onClick={handleCopy} title="Copy message" style={copyBtnBase}>
          {isCopied ? <CheckIcon /> : <CopyIcon />}
        </button>
      </div>
    </div>
  );
};

export default MessageBubble;
